/**
 * Conviction → display-percentage helpers. The SINGLE place an on-chain
 * conviction value (PPB) becomes a human percentage, shared by every Morfi
 * consumer — web charts, market row cards, the featured hero, resolved-market
 * standings, and the SDK conviction debug panel — so the number is
 * byte-for-byte identical everywhere. No consumer
 * may re-derive a conviction percentage itself; that drift is exactly the bug
 * this module exists to kill.
 *
 * Grounded in the contract (contracts/markets/.../trading/conviction.rs):
 *  - Conviction is a PER-OUTCOME gauge in PPB where {@link CONVICTION_SCALE_PPB}
 *    (1e9) = 100% (conviction.rs:9-10).
 *  - Display conviction is NOT a share. Per-outcome values do NOT sum to 100%
 *    — a single outcome ranges 0%–200% (capped at MAX_EVENT_CONVICTION_PPB =
 *    2e9, conviction.rs:11). NEVER normalise this display value or chart line
 *    by the cross-outcome sum.
 *  - Resolution itself uses a normalized leader-share predicate
 *    (`leader * 1e9 >= threshold * total_conviction`). That predicate belongs
 *    in the ending-soon / crank projection helpers, not in these display
 *    percent helpers. A resolved winner can still legitimately read above 100%
 *    (e.g. 127.7%). NEVER clamp the display value to 100%.
 *  - After resolution the contract never changes conviction again
 *    (resolution.rs:110; the per-outcome array is left frozen) — see
 *    {@link isTerminalMarket}, which the projector uses to freeze it.
 */

import { CONVICTION_SCALE_PPB, isTerminalMarket } from './conviction.js';

/** 1e9 as a number — PPB full scale (100%). Display math runs in the number
 *  domain (float-at-the-edge per .claude/rules/numeric-precision.md rule 6);
 *  conviction magnitudes (≤2e9) are exactly representable as f64. */
const SCALE: number = Number(CONVICTION_SCALE_PPB);

function toFiniteNonNegative(ppb: string | number | bigint | null | undefined): number {
  if (ppb === null || ppb === undefined) return 0;
  const n = typeof ppb === 'bigint' ? Number(ppb) : Number(ppb);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/**
 * Raw conviction PPB → a 0→~2.0 FRACTION (ppb ÷ 1e9). For chart series, axis
 * inputs, and progress fills that expect a 0–1+ fraction. Never normalised,
 * never clamped — a winner returns >1.0.
 */
export function convictionFraction(ppb: string | number | bigint | null | undefined): number {
  return toFiniteNonNegative(ppb) / SCALE;
}

/**
 * Raw conviction PPB → a 0→~200 PERCENT (ppb ÷ 1e9 × 100), floored at 0 and
 * rounded to 2 decimals (keeps near-tied outcomes distinguishable without
 * float noise like 9.000000000000002). NEVER normalised across outcomes and
 * NEVER clamped to 100 — the winner reads above 100% by design.
 */
export function convictionPercent(ppb: string | number | bigint | null | undefined): number {
  return Math.round(convictionFraction(ppb) * 100 * 100) / 100;
}

/** Minimal market shape needed to decide whether conviction is frozen.
 *  Optionals admit `undefined` explicitly so callers under
 *  `exactOptionalPropertyTypes` can pass `{ status: maybeUndefined }`. */
export interface ConvictionMarketFields {
  readonly status?: string | undefined;
  readonly resolvedAt?: string | null | undefined;
}

/** Minimal outcome shape the summary surfaces read for their display %. */
export interface ConvictionOutcomeFields {
  readonly conviction?: string | null | undefined;
  /** Backend-served snapshot of conviction at the resolution instant. Present
   *  on the detail hook (`useGetMarket`); absent on the feed (`useGetMarkets`),
   *  where the SDK projector has already frozen `conviction` to this value. */
  readonly convictionAtResolution?: string | null | undefined;
}

/** True when a market's conviction is frozen on-chain (resolved/graduated) —
 *  re-exported {@link isTerminalMarket} under a consumer-facing name. */
export function isMarketTerminal(market: ConvictionMarketFields): boolean {
  return isTerminalMarket(market);
}

/**
 * The conviction PPB to DISPLAY for an outcome on a summary surface (market row
 * card, featured hero). For a terminal market this PINS to the conviction at
 * resolution — `convictionAtResolution` when the hook provides it, otherwise
 * the frozen live `conviction` (the feed hook omits the snapshot field, but the
 * SDK projector freezes resolved markets so `conviction` already equals it).
 * For a live market it's the live `conviction`.
 */
export function resolvedOutcomeConvictionPpb(
  outcome: ConvictionOutcomeFields,
  market: ConvictionMarketFields,
): string {
  if (isMarketTerminal(market)) {
    const atResolution = outcome.convictionAtResolution;
    if (atResolution !== null && atResolution !== undefined && atResolution !== '') {
      return atResolution;
    }
  }
  return outcome.conviction ?? '0';
}

/** Resolution-aware {@link convictionPercent} for summary surfaces — pins to the
 *  conviction at resolution for terminal markets, live conviction otherwise. */
export function outcomeConvictionPercent(
  outcome: ConvictionOutcomeFields,
  market: ConvictionMarketFields,
): number {
  return convictionPercent(resolvedOutcomeConvictionPpb(outcome, market));
}

/** Resolution-aware {@link convictionFraction} for summary surfaces. */
export function outcomeConvictionFraction(
  outcome: ConvictionOutcomeFields,
  market: ConvictionMarketFields,
): number {
  return convictionFraction(resolvedOutcomeConvictionPpb(outcome, market));
}
